<template>
  <div>
    <!-- ===================== MOBILE CARD LIST (< 960px) ===================== -->
    <div class="d-md-none oem-watchlist-mobile-list mt-2 px-2 pb-4">
      <v-progress-linear v-if="loading" indeterminate color="red" class="mb-2" />

      <div
        v-for="(item, index) in paginatedWatchlist"
        :key="item._id || `${item.oemCode}-${index}`"
        class="oem-watchlist-mobile-card"
        :class="{ 'oem-watchlist-mobile-card--below': item.belowMinimum }"
      >
        <div class="oem-watchlist-mobile-card__main">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-btn color="black" variant="outlined" size="x-small" rounded readonly @click.stop>
              {{ item.oemCode }}
            </v-btn>
            <v-chip size="small" variant="tonal">
              {{ $t("oem_stock_watchlist.table.active_count") }}:
              <strong class="ml-1">{{ item.activeCount }}</strong>
            </v-chip>
            <v-chip size="small" variant="tonal">
              {{ $t("oem_stock_watchlist.table.minimum") }}:
              <strong class="ml-1">{{ item.minimumQuantity }}</strong>
            </v-chip>
          </div>

          <div class="text-caption text-grey-darken-1 mt-2">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("oem_stock_watchlist.table.status") }}:
            </span>
            <v-chip
              v-if="item.belowMinimum"
              color="red"
              size="small"
              variant="flat"
              class="ml-1"
            >
              {{ $t("oem_stock_watchlist.below_minimum") }} ({{ item.shortfall }})
            </v-chip>
            <span v-else>—</span>
          </div>

          <div class="text-caption text-grey-darken-1 mt-1">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("oem_stock_watchlist.table.category") }}:
            </span>
            {{ localizedLabel(item.category) || "—" }}
          </div>
          <div class="text-caption text-grey-darken-1 mt-1">
            <span class="font-weight-medium text-grey-darken-2">
              {{ $t("oem_stock_watchlist.table.title") }}:
            </span>
            {{ localizedLabel(item.title) || "—" }}
          </div>

          <div class="mt-2 d-flex align-center flex-wrap ga-2">
            <template v-if="!isReadOnly">
              <v-switch
                :model-value="item.enabled"
                color="red-darken-1"
                hide-details
                density="compact"
                :loading="togglingId === item._id"
                @update:model-value="toggleEnabled(item, $event)"
              />
              <v-spacer />
              <v-btn icon variant="text" size="small" @click.stop="editEntry(item)">
                <v-icon size="20" color="grey-darken-2">mdi-pencil</v-icon>
              </v-btn>
              <v-btn icon variant="text" size="small" @click.stop="deleteEntry(item._id)">
                <v-icon size="20" color="grey-darken-2">mdi-delete</v-icon>
              </v-btn>
            </template>
            <v-chip v-else size="small" :color="item.enabled ? 'green' : 'grey'">
              {{
                item.enabled
                  ? $t("oem_stock_watchlist.form.enabled_yes")
                  : $t("oem_stock_watchlist.form.enabled_no")
              }}
            </v-chip>
          </div>
        </div>
      </div>

      <v-alert v-if="!loading && paginatedWatchlist.length < 1" type="info" variant="tonal" class="mt-2">
        No data available.
      </v-alert>
    </div>
    <!-- ===================================================================== -->

    <!-- Desktop table (unchanged) -->
    <v-data-table
      class="d-none d-md-block elevation-1 pb-8"
      :headers="tableHeaders"
      :items="watchlist"
      :loading="loading"
      :items-per-page="-1"
      hide-default-footer
    >
      <template #progress>
        <v-progress-linear color="red" indeterminate />
      </template>
      <template #item="{ item, index }">
        <tr
          :class="[
            item.belowMinimum
              ? 'below-minimum-row'
              : index % 2 === 0
                ? 'bg-grey-lighten-4'
                : '',
          ]"
        >
          <td class="text-xs">
            <v-btn variant="outlined" rounded>
              {{ item.oemCode }}
            </v-btn>
          </td>
          <td class="text-xs">
            <v-chip>{{ item.activeCount }}</v-chip>
          </td>
          <td class="text-xs">
            {{ item.minimumQuantity }}
          </td>
          <td class="text-xs">
            <v-chip v-if="item.belowMinimum" color="red" variant="flat">
              {{ $t("oem_stock_watchlist.below_minimum") }}
              ({{ item.shortfall }})
            </v-chip>
            <span v-else>—</span>
          </td>
          <td class="text-xs">
            {{ localizedLabel(item.category) || "—" }}
          </td>
          <td class="text-xs">
            {{ localizedLabel(item.title) || "—" }}
          </td>
          <td class="text-xs">
            <v-switch
              v-if="!isReadOnly"
              :model-value="item.enabled"
              color="red-darken-1"
              hide-details
              density="compact"
              :loading="togglingId === item._id"
              @update:model-value="toggleEnabled(item, $event)"
            />
            <v-chip v-else size="small" :color="item.enabled ? 'green' : 'grey'">
              {{ item.enabled ? $t("oem_stock_watchlist.form.enabled_yes") : $t("oem_stock_watchlist.form.enabled_no") }}
            </v-chip>
          </td>
          <td v-if="!isReadOnly" class="text-xs">
            <v-icon
              class="mr-2 pointer"
              color="grey-darken-2"
              @click="editEntry(item)"
            >
              mdi-pencil
            </v-icon>
            <v-icon
              class="pointer"
              color="grey-darken-2"
              @click="deleteEntry(item._id)"
            >
              mdi-delete
            </v-icon>
          </td>
        </tr>
      </template>
    </v-data-table>

    <!-- Mobile pagination footer (keep pagination like Inventory) -->
    <div class="oem-watchlist-table-host pb-2">
      <v-data-table
        v-model:options="pagination"
        :headers="tableHeaders"
        :items="watchlist"
        :loading="loading"
        :items-per-page="pagination.itemsPerPage"
        :items-per-page-options="[
          { value: 5, title: '5' },
          { value: 10, title: '10' },
          { value: 25, title: '25' },
          { value: 50, title: '50' },
        ]"
        items-per-page-text="Rows per page"
        class="elevation-1"
        @update:options="updatePagination"
      >
        <template #progress>
          <v-progress-linear color="red" indeterminate />
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="dialogDelete" width="auto">
      <v-card>
        <v-card-title class="text-h6">
          <v-alert
            class="mb-0"
            :model-value="true"
            type="error"
            variant="tonal"
            border="bottom"
          >
            {{ $t("delete_alert") }}
          </v-alert>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogDelete = false">
            {{ $t("close") }}
          </v-btn>
          <v-btn color="red" variant="outlined" @click="confirmDelete">
            {{ $t("delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";
import deleteRequest from "../../api/deleteRequest";

const ADMIN_ROLES = ["LekoAdmin", "LekoEmployee", "SuperAdmin"];
const INSTITUTION_SELECTOR_ROLES = ["LekoAdmin", "SuperAdmin"];

export default {
  props: {
    belowOnly: {
      type: Boolean,
      default: false,
    },
    institutionId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    loading: false,
    togglingId: null,
    watchlist: [],
    pagination: {
      itemsPerPage: 10,
      page: 1,
      sortBy: [{ key: "belowMinimum", order: "desc" }],
      sortDesc: [true],
    },
    dialogDelete: false,
    entryToDelete: null,
    headers: [
      { title: "", sortable: false, key: "oemCode" },
      { title: "", sortable: false, key: "activeCount" },
      { title: "", sortable: false, key: "minimumQuantity" },
      { title: "", sortable: false, key: "belowMinimum" },
      { title: "", sortable: false, key: "category" },
      { title: "", sortable: false, key: "title" },
      { title: "", sortable: false, key: "enabled" },
      { title: "", sortable: false, key: "actions" },
    ],
  }),
  computed: {
    isReadOnly() {
      return INSTITUTION_SELECTOR_ROLES.includes(this.$store.getters.role);
    },
    tableHeaders() {
      const headers = [...this.headers];
      if (this.isReadOnly) {
        return headers.filter((h) => h.key !== "actions");
      }
      return headers;
    },
    paginatedWatchlist() {
      const items = [...(this.watchlist || [])];
      // Put belowMinimum first on mobile cards (then oem code)
      items.sort((a, b) => {
        const am = a?.belowMinimum ? 1 : 0;
        const bm = b?.belowMinimum ? 1 : 0;
        if (am !== bm) return bm - am;
        return String(a?.oemCode || "").localeCompare(String(b?.oemCode || ""));
      });
      const page = this.pagination.page || 1;
      const perPage = this.pagination.itemsPerPage || 10;
      const start = (page - 1) * perPage;
      return items.slice(start, start + perPage);
    },
  },
  watch: {
    belowOnly() {
      this.getWatchlist();
    },
    institutionId() {
      this.getWatchlist();
    },
    "$i18n.locale"() {
      this.getLocale();
    },
  },
  created() {
    this.getLocale();
    this.getWatchlist();
  },
  methods: {
    localizedLabel(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      const lang = this.$store.getters.language;
      return value[lang] || value.it || value.en || "";
    },
    isGlobalAdmin() {
      return ADMIN_ROLES.includes(this.$store.getters.role);
    },
    usesSelectedInstitution() {
      return INSTITUTION_SELECTOR_ROLES.includes(this.$store.getters.role);
    },
    buildQuery(extra = "") {
      let query = extra;
      if (this.belowOnly) {
        query = query ? `${query}&belowOnly=true` : "belowOnly=true";
      }
      if (this.usesSelectedInstitution()) {
        if (!this.institutionId) return null;
        const inst = `institution_id=${this.institutionId}`;
        query = query ? `${query}&${inst}` : inst;
      } else if (this.isGlobalAdmin()) {
        const inst = `institution_id=${this.$store.getters.institutionId}`;
        query = query ? `${query}&${inst}` : inst;
      }
      return query;
    },
    getLocale() {
      this.headers[0].title = this.$t("oem_stock_watchlist.table.oem");
      this.headers[1].title = this.$t("oem_stock_watchlist.table.active_count");
      this.headers[2].title = this.$t("oem_stock_watchlist.table.minimum");
      this.headers[3].title = this.$t("oem_stock_watchlist.table.status");
      this.headers[4].title = this.$t("oem_stock_watchlist.table.category");
      this.headers[5].title = this.$t("oem_stock_watchlist.table.title");
      this.headers[6].title = this.$t("oem_stock_watchlist.table.enabled");
      this.headers[7].title = this.$t("oem_stock_watchlist.table.actions");
    },
    getWatchlist() {
      const query = this.buildQuery();
      if (query === null) {
        this.watchlist = [];
        return;
      }
      this.loading = true;
      getRequest("/oem-stock-watchlist", query)
        .then((response) => {
          this.watchlist = response.watchlist || [];
          this.pagination.page = 1;
        })
        .catch((error) => {
          this.$emit("error", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    editEntry(data) {
      this.$emit("edit", data);
    },
    toggleEnabled(item, enabled) {
      this.togglingId = item._id;
      const body = { enabled };
      putRequest(
        `/oem-stock-watchlist/${item._id}`,
        this.buildQuery(),
        body,
      )
        .then(() => {
          this.getWatchlist();
        })
        .catch((error) => {
          this.$emit("error", error);
        })
        .finally(() => {
          this.togglingId = null;
        });
    },
    deleteEntry(id) {
      this.entryToDelete = id;
      this.dialogDelete = true;
    },
    confirmDelete() {
      this.loading = true;
      deleteRequest(
        `/oem-stock-watchlist/${this.entryToDelete}`,
        this.buildQuery(),
      )
        .then(() => {
          this.dialogDelete = false;
          this.entryToDelete = null;
          this.getWatchlist();
        })
        .catch((error) => {
          this.$emit("error", error);
        })
        .finally(() => {
          this.loading = false;
          this.dialogDelete = false;
          this.entryToDelete = null;
        });
    },
    updatePagination(options) {
      this.pagination = {
        ...this.pagination,
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 10,
        page: options.page ? options.page : 1,
      };
    },
  },
};
</script>

<style scoped>
.below-minimum-row {
  background-color: #ffebee !important;
}

@media (max-width: 959.98px) {
  .oem-watchlist-table-host :deep(.v-table__wrapper),
  .oem-watchlist-table-host :deep(.v-data-table__wrapper),
  .oem-watchlist-table-host :deep(.v-data-table__wrapper__scroll) {
    display: none !important;
  }
  .oem-watchlist-table-host :deep(.v-data-table-footer) {
    padding-inline: 12px;
  }
}
@media (min-width: 960px) {
  .oem-watchlist-mobile-list,
  .oem-watchlist-table-host {
    display: none !important;
  }
}

.oem-watchlist-mobile-card {
  position: relative;
  background: #fff;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}
.oem-watchlist-mobile-card--below {
  background-color: #ffebee !important;
}
.oem-watchlist-mobile-card__main {
  padding: 10px;
}
</style>
